import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Box } from "@mui/material";
import { useMyHrEmployee } from "my-hr/hooks";
import { MyTimeCalendar, MyTimeInfoDialog } from "my-time/components";
import {
  employeeAdmissionsByAdmin,
  employeeAdmissionsByAdminIsMobile,
} from "my-time/configs/table-defs";
import {
  useMyTimeActualDate,
  useMyTimeAdmission,
  useMyTimeRecordingPeriod,
} from "my-time/hooks";
import { parseActions } from "my-time/utils";
import { countDailyHours } from "my-time/utils";
import { OcCard, OcDataTable } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import { useOcButtonGroup, useOcTableState, useOcProperty } from "oc/hooks";
import { getFullname } from "oc/utils";

type MyTimeAdmissionCardByAdminProps = {
  contractCode: string;
  employeeCode: string;
  year: number;
  month: number;
  changeMyTimeEnabled: boolean;
  changeWorkingTimeEnabled: boolean;
  isMobile: boolean;
};

export const MyTimeAdmissionCardByAdmin = ({
  contractCode,
  employeeCode,
  year,
  month,
  changeMyTimeEnabled,
  changeWorkingTimeEnabled: editWorkingTimeEnabled,
  isMobile,
}: MyTimeAdmissionCardByAdminProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const recordingPeriod = useMyTimeRecordingPeriod();

  const [selectedRowId, setSelectedRowId] = useState<
    string | number | undefined
  >(undefined);

  const buttonsDef = ["OC_CALENDAR", "OC_TABLE"];
  const { OcButtonGroup, selectedButton, handleButtonChange } =
    useOcButtonGroup({
      id: `MyTimeAdmissionCard`,
      buttonsDef,
    });

  useEffect(() => {
    if (isMobile) {
      handleButtonChange(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  const { data: employeeData } = useMyHrEmployee({ employeeId: employeeCode });
  const employeeName = getFullname(employeeData);

  const { data: myTimeEnabledData } = useOcProperty({
    resource: "myTime",
    key: "changeMyTimeEnabled",
  });

  const requestsEnabled = myTimeEnabledData === "true" ? true : false;

  const [openInfoDialog, setOpenInfoDialog] = useState({
    state: false,
    data: null,
  });

  const { data, events, loading, error } = useMyTimeAdmission({
    contractCode,
    year,
    month,
    changeMyTimeEnabled,
    recordingPeriod,
  });

  const countTimeData = (date: string) => {
    const result = countDailyHours({ date, list: data });
    return result;
  };

  const parsedData = data?.map((item) => {
    const newItem = { ...item };
    newItem.countTimeData = countTimeData;
    return newItem;
  });

  const list = {
    rows: parsedData,
    total: data?.length,
  };

  const handleSelectRow = ({ action, row }: { action: string; row: any }) => {
    parseActions({
      requestsEnabled,
      editWorkingTimeEnabled,
      action,
      row,
      list,
      role: "examiner",
      setOpenNewRequestDialog: () => { },
      setOpenInfoDialog,
      setOpenShortInfoDialog: () => { },
      history,
      employeeCode,
      contractCode,
    });
    return;
  };

  let tableId = "employeeAdmissionsTableData";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "title",
    pageSize: 5,
  });

  if (employeeAdmissionsByAdmin && employeeAdmissionsByAdmin?.columns) {
    type MyTimeActionColumn =
      (typeof employeeAdmissionsByAdmin.columns)[keyof typeof employeeAdmissionsByAdmin.columns] & {
        changeMyTimeEnabled?: boolean;
        changeWorkingTimeEnabled?: boolean;
      };

    const defValues = Object.values(employeeAdmissionsByAdmin?.columns);
    let myTimeActions: MyTimeActionColumn | null = null;
    const newDef = defValues.map((item) => {
      if (item?.id === "myTimeActions" || item?.id === "myTimeAdminActions") {
        myTimeActions = {
          ...item,
          changeMyTimeEnabled,
          changeWorkingTimeEnabled: editWorkingTimeEnabled,
        };
        return myTimeActions;
      }
      return item;
    });
    employeeAdmissionsByAdmin.columns = newDef;
  }

  const { date, handleDateChange } = useMyTimeActualDate();

  const handleMoreClick = () => {
    handleButtonChange(1);
  };

  const onSelectRow = (o: any) => {
    if (isMobile) {
      setSelectedRowId(selectedRowId === o?._id ? undefined : o?._id);
    }
  };

  const tableDef = isMobile
    ? employeeAdmissionsByAdminIsMobile
    : employeeAdmissionsByAdmin;

  const filteredTableDef = {
    id: employeeAdmissionsByAdmin?.id,
    columns: employeeAdmissionsByAdmin?.columns?.filter(
      (column) =>
        !employeeAdmissionsByAdminIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id,
        ),
    ),
  };

  return (
    <OcCard
      label={t("MY_TIME_MY_WORK_TIME_REPORTS")}
      loading={loading}
      error={!!error}
    >
      <MyTimeInfoDialog
        open={openInfoDialog?.state}
        handleClose={() => setOpenInfoDialog({ state: false, data: null })}
        data={openInfoDialog?.data}
        employeeName={employeeName ?? ""}
        isMobile={isMobile}
      />
      {!isMobile && (
        <Box
          id="button-group"
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          padding="18px 0px"
        >
          <OcButtonGroup />
        </Box>
      )}
      <Box padding="16px 0px">
        {selectedButton === "OC_TABLE" && (
          <OcDataTable
            tableDef={tableDef}
            tableId={tableId}
            tableData={tableData}
            setTableData={setTableData}
            data={list}
            loading={loading}
            onSelectRow={isMobile ? onSelectRow : handleSelectRow} //For some reason it didn't want to work based on the established logic, that's why I decided on ternary.
            filteredTableDef={filteredTableDef}
            selectedRowId={selectedRowId}
            isNotExaminer={true}
          />
        )}
        {selectedButton === "OC_CALENDAR" && (
          <MyTimeCalendar
            date={date}
            contract={contractCode}
            onDateChange={handleDateChange}
            events={events}
            handleSelectRow={handleSelectRow}
            handleMoreClick={handleMoreClick}
            onClickDisabled={true}
            isNotExaminer={false}
            countTimeData={countTimeData}
          />
        )}
      </Box>
    </OcCard>
  );
};

export default MyTimeAdmissionCardByAdmin;
